const constants = require('./constants.js');
const breakpoints = constants.breakpoints;
const breakpointHeights = constants.breakpointHeights;

/**
 * addScriptResource injects a script tag in document.head.
 *
 * Inspired by Puppeteer's addScriptTag.
 * It returns a promise of the script element and is resolved by the script element load event.
 *
 * @param {string} resourceUrl - URL of the script resource to load.
 * @param {string} scriptAttrMap - Optional, adds key value pairs of map as name / value of attribute
 * @param {HTMLElement} hostElement - Optional, HTMLElement: Inject script element into this node.
 * @returns {Promise<ScriptElement>} - Promise of the script DOM element
 */
function addScriptResource(
    resourceUrl,
    scriptAttrMap = {},
    hostElement = document.head,
    hostDocument = document
) {
    return new Promise((resolve, reject) => {
        const element = hostDocument.createElement('script');
        for (let key in scriptAttrMap) {
            element.setAttribute(key, scriptAttrMap[key]);
        }
        element.onload = function () {
            resolve(element);
        };
        element.onerror = function (err) {
            reject(err);
        };
        element.type = 'text/javascript';
        element.src = resourceUrl;
        hostElement.appendChild(element);
    });
}

const _addScriptResourceOncePromises = {};

/**
 * Same as addScriptResource, except that the script is loaded only once if called multiple times.
 * The deduplication key is scriptId with a fallback on resourceUrl.
 */

function addScriptResourceOnce(resourceUrl, scriptAttrMap = {}, hostElement, hostDocument) {
    const key = scriptAttrMap.id || resourceUrl;
    return (
        _addScriptResourceOncePromises[key] ||
        (_addScriptResourceOncePromises[key] = addScriptResource(
            resourceUrl,
            scriptAttrMap,
            hostElement,
            hostDocument
        ))
    );
}

/**
 * regex-based extraction of hostname with some normalization, as for referral
 * log parsing or allow/denylist behavior. Taken from the now-defunct
 * tempest-delivery repository.
 */
function parseHostName(url) {
    return (
        url
            // Trim the leading protocol
            .replace(/^https?:\/\//, '')
            // Save the URL path
            // Trim the trailing colon, and all slashes
            .replace(/[:\/].*/, '')
            .toLowerCase()
            // Remove the WORLD WIDE WEB (www) standard prefix
            .replace(/^www\./, '')
            // Remove mobile (m) subdomain
            .replace(/^m\./, '')
            // <before>.blogspot.<after> -> <before>.blogspot.com
            .replace(/\.blogspot(\.com?)?\.[a-z]{2,3}$/, '.blogspot.com')
            // <before>.gamepedia.<after> -> gamepedia.com
            // <before>.wikia.<after> -> wikia.com
            .replace(/^.*\.(gamepedia|wikia)(\.com?)?\.[a-z]{2,3}$/, '$1.com')
            // Keep at most three domain segments, starting from the right
            .split('.')
            .slice(-3)
            .join('.')
    );
}

/**
 * GetCurrentBreakpoint returns the current breakpoint.
 * *
 * @returns {string} - 'A', 'B', 'C', or 'D'
 */
function getCurrentBreakpoint() {
    let breakpoint;
    const width = window.innerWidth;
    const height = window.innerHeight;
    for (let possibleBreakpoint in breakpoints) {
        if (width >= breakpoints[possibleBreakpoint]) {
            if (!breakpointHeights[possibleBreakpoint] || height >= breakpointHeights[possibleBreakpoint]) {
                breakpoint = possibleBreakpoint;
            }
        }
    }
    return breakpoint;
}

/**
 * BreakpointObserver listens to the document 'resize' event and uses
 * getCurrentBreakpoint to detect breakpoint changes and notify subscribers.
 *
 * IDEA: Ideally, this would use the ResizeObserver API.
 * (see https://developers.google.com/web/updates/2016/10/resizeobserver)
 * But only Chrome supports it right now, and loading a polyfill for only
 * one use case (currently only <phoenix-message-banner>) feels too heavy.
 * This should be revisited for more important use cases, like <phoenix-ad>.
 */
class BreakpointObserver {
    constructor() {
        // currently observed breakpoint
        this.observedBreakpoint = undefined;

        // objects subscribing to change callbacks
        this.subscribers = [];

        this._observe();
    }

    _observe() {
        this.observedBreakpoint = getCurrentBreakpoint();
        window.addEventListener('resize', () => {
            const breakpoint = getCurrentBreakpoint();
            if (breakpoint !== this.observedBreakpoint) {
                this.observedBreakpoint = breakpoint;
                this.subscribers.forEach((subscriber) => {
                    subscriber.onBreakpointChange(breakpoint);
                });
            }
        });
    }

    subscribe(subscriber) {
        if (!subscriber.onBreakpointChange) {
            throw new Error('onBreakpointChange method required');
        }
        this.unsubscribe(subscriber);
        this.subscribers.push(subscriber);
    }

    unsubscribe(subscriber) {
        const index = this.subscribers.indexOf(subscriber);
        this.subscribers.splice(index, 1);
    }
}

// global BreakpointObserver object for observeBreakpointChanges.
let breakpointObserver;

/**
 * observeBreakpointChanges uses BreakpointObserver to detect the breakpoint changes,
 * and run "onBreakpointChange" on the element.
 *
 * This function returns the observer, which allows the caller to unsubscribe:
 *   observer.unsubscribe(element);
 *
 * @param {PhoenixElement} element - the element to observe.
 * @returns {BreakpointObserver} - the observer used or created.
 */
function observeBreakpointChanges(element) {
    if (!breakpointObserver) {
        breakpointObserver = new BreakpointObserver();
    }
    breakpointObserver.subscribe(element);
    return breakpointObserver;
}

function _isDOMReady() {
    return document.readyState === 'complete' || document.readyState === 'interactive';
}

var _DOMContentLoadedPromise;

/**
 * whenDomContentLoaded waits for the DOM content to be loaded.
 *
 * Resolve immediately if the document.readyState indicates that the DOM content is loaded. Or listen
 * to the document.DOMContentLoaded event.
 *
 * @returns {Promise} - resolve when the DOM content is loaded.
 */
function whenDomContentLoaded() {
    if (!_DOMContentLoadedPromise) {
        if (_isDOMReady()) {
            _DOMContentLoadedPromise = Promise.resolve();
        } else {
            _DOMContentLoadedPromise = new Promise((resolve, reject) => {
                document.addEventListener('DOMContentLoaded', resolve);
            });
        }
    }
    return _DOMContentLoadedPromise;
}

function _isPageLoaded() {
    return document.readyState === 'complete';
}

var _pageLoadedPromise;

/**
 * whenPageLoaded waits for the page to be loaded.
 *
 * Resolve immediately if the document.readyState indicates that the page is loaded. Or listen
 * to the window.load event.
 *
 * @returns {Promise} - resolve when the page is loaded.
 */
function whenPageLoaded() {
    if (!_pageLoadedPromise) {
        if (_isPageLoaded()) {
            _pageLoadedPromise = Promise.resolve();
        } else {
            _pageLoadedPromise = new Promise((resolve, reject) => {
                window.addEventListener('load', resolve);
            });
        }
    }
    return _pageLoadedPromise;
}

function _querySelectorWithObserver(rootElement, query, moOptions, cancelCallback) {
    const element = rootElement.querySelector(query);
    if (element) {
        return Promise.resolve(element);
    } else {
        return new Promise((resolve, reject) => {
            const observer = new MutationObserver(() => {
                const element = rootElement.querySelector(query);
                if (element) {
                    observer.disconnect();
                    resolve(element);
                    return;
                }
                if (cancelCallback) {
                    cancelCallback(() => {
                        observer.disconnect();
                        reject(new Error('element not found'));
                        return;
                    });
                }
            });
            observer.observe(rootElement, moOptions);
        });
    }
}

/**
 * waitForElement is a wrapper around Element.querySelector with the ability to observe DOM changes.
 *
 * The main use case is when a custom element implementation depends on its subtree. If the custom
 * element class is already available, and the DOM is being parsed, then the connectedCallback of
 * this element will be called before the subtree is parsed.
 *
 * This function allows the custom element implementation to wait for an element of the subtree.
 * It does it by using a Mutation Observer, with the option to cancel the observation when the full
 * document is parsed (DOM ready).
 *
 * @param {HTMLElement} rootElement - run querySelector from this element.
 * @param {string} query - run querySelector with this query.
 * @param {Object} options - See below.
 * @returns {Promise} - Promise of the element found by querySelector.
 *
 * options.untilDomReady = true cancels the Mutation Observer at the first observation after the
 * document is ready.
 *
 */
function waitForElement(rootElement, query, options) {
    if (!rootElement) {
        throw new Error('rootElement required');
    }

    var cancelCallback;
    if (options && options.untilDomReady) {
        cancelCallback = (cancel) => {
            if (_isDOMReady()) {
                cancel();
            }
        };
    }

    // This can potentially be exposed in "options" to let the caller customize
    // the Mutation Observer.
    const moOptions = {
        childList: true,
        subtree: true,
    };

    return _querySelectorWithObserver(rootElement, query, moOptions, cancelCallback);
}

// Margin around the root element (here the document)
// See: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
// Lighthouse has an audit rule to detect "offscreen" images. This rule uses a 200 pixels
// margin. In order to not be penalized by Lighthouse we set a margin that is just below.
const defaultRootMargin = '190px 0px';

// Global registry of IntersectionObserver objects by rootMargin for observeFirstIntersection.
let firstIntersectionObservers = {};

/**
 * makeIntersectionObserver creates an IntersectionObserver with the provided options.
 * If the IntersectionObserver constructor throws an error, it tries to create an
 * IntersectionObserver without the root property, since some browsers do not support
 * the 'document' object as a root.
 * @param {Function} callback - IntersectionObserver callback.
 * @param {Object} options - IntersectionObserver options.
 */
function makeIntersectionObserver(callback, options) {
    let observer;
    try {
        observer = new IntersectionObserver(callback, options);
    } catch {
        const safeOptions = { ...options };
        delete safeOptions.root;
        observer = new IntersectionObserver(callback, safeOptions);
    }
    return observer;
}

/**
 * observeFirstIntersection uses IntersectionObservers to detect the first intersection
 * for the provided element, and run "onFirstIntersection" on the element.
 *
 * The element is unobserved at the first observation, just before running the callback.
 *
 * The rootMargin option can be specified. Technically a new observer is created for each
 * rootMargin value.
 *
 * This function returns the observer, which allows the caller to unobserve the element:
 *   observer.unobserve(element);
 *
 * @param {PhoenixElement} element - the element to observe.
 * @param {string} rootMargin - the IntersectionObserver rootMargin, defaults to '190px 0px'.
 * @returns {IntersectionObserver} - the observer used or created.
 */
function observeFirstIntersection(element, rootMargin = defaultRootMargin, root = document) {
    const observer =
        firstIntersectionObservers[rootMargin] ||
        (firstIntersectionObservers[rootMargin] = makeIntersectionObserver(
            (entries, cbObserver) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        cbObserver.unobserve(entry.target);
                        if (entry.target.onFirstIntersection) {
                            entry.target.onFirstIntersection();
                        }
                    }
                });
            },
            { root, rootMargin }
        ));
    observer.observe(element);
    return observer;
}

// global IntersectionObserver object for observeVisibilityChanges.
let visibilityObserver;

/**
 * observeAfterFirstFullView uses IntersectionObservers to detect when the target
 * was rendered completely(100%) in the viewport for the first time,
 * then run "onAfterFirstFullView" on the observed element.
 */
function observeAfterFirstFullView(element) {
    const observer = makeIntersectionObserver(
        (entries, cbObserver) => {
            entries.forEach((entry) => {
                if (entry.target.onAfterFirstFullView) {
                    if (entry.isIntersecting) {
                        entry.target.onAfterFirstFullView(entry);
                        cbObserver.unobserve(entry.target);
                    }
                }
            });
        },
        {
            root: document,
            threshold: [0.95, 1],
        }
    );
    observer.observe(element);
    return observer;
}

/**
 * observeVisibilityChanges uses IntersectionObservers to detect the visibility changes
 * for the provided element, and run "onChangeToVisible", and "onChangeToNotVisible" on
 * the element.
 *
 * The threshold is set to 0.5. The element is considered visible if at least 50% of it
 * intersects with the viewport.
 *
 * This function returns the observer, which allows the caller to unobserve the element:
 *   observer.unobserve(element);
 *
 * @param {PhoenixElement} element - the element to observe.
 * @returns {IntersectionObserver} - the observer used or created.
 */
function observeVisibilityChanges(element) {
    if (!visibilityObserver) {
        visibilityObserver = makeIntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        if (entry.target.onChangeToVisible) {
                            entry.target.onChangeToVisible();
                        }
                    } else {
                        if (entry.target.onChangeToNotVisible) {
                            entry.target.onChangeToNotVisible();
                        }
                    }
                });
            },
            // trigger visibility once the element is 50%+ within the browser's viewport.
            { threshold: 0.5 }
        );
    }
    visibilityObserver.observe(element);
    return visibilityObserver;
}

/**
 * userHasPremium checks the phxConfigQueue to see if the current user is a Premium user.
 * This is asynchronous, and returns true or false.
 *
 * @returns {Promise} - Promise of the boolean
 */
function userHasPremium() {
    return new Promise((resolve, reject) => {
        if (!window.phxConfigQueue) {
            reject('config object is necessary to check premium');
        }
        window.phxConfigQueue.push(function () {
            resolve(this.user.hasPremium());
        });
    });
}

/**
 * debounce - adapted from lodash - http://lodash.org
 */
function debounce(func, wait, options) {
    let args,
        leading,
        maxTimeoutId,
        result,
        stamp,
        thisArg,
        timeoutId,
        trailingCall,
        lastCalled = 0,
        maxWait = false,
        trailing = true;

    // lodash helper functions brought into debounce.
    function now() {
        return new Date().getTime();
    }

    function isObject(value) {
        // Avoid a V8 JIT bug in Chrome 19-20.
        // See https://code.google.com/p/v8/issues/detail?id=2291 for more details.
        var type = typeof value;
        return !!value && (type === 'object' || type === 'function');
    }

    function cancel() {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
        if (maxTimeoutId) {
            window.clearTimeout(maxTimeoutId);
        }
        maxTimeoutId = timeoutId = trailingCall = undefined;
    }

    function pending() {
        return timeoutId !== undefined;
    }

    function delayed() {
        var remaining = wait - (now() - stamp);
        if (remaining <= 0 || remaining > wait) {
            if (maxTimeoutId) {
                window.clearTimeout(maxTimeoutId);
            }
            var isCalled = trailingCall;
            maxTimeoutId = timeoutId = trailingCall = undefined;
            if (isCalled) {
                lastCalled = now();
                result = func.apply(thisArg, args);
                if (!timeoutId && !maxTimeoutId) {
                    args = thisArg = null;
                }
            }
        } else {
            timeoutId = window.setTimeout(delayed, remaining);
        }
    }

    function maxDelayed() {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
        maxTimeoutId = timeoutId = trailingCall = undefined;
        if (trailing || maxWait !== wait) {
            lastCalled = now();
            result = func.apply(thisArg, args);
            if (!timeoutId && !maxTimeoutId) {
                args = thisArg = null;
            }
        }
    }

    function debounced() {
        var leadingCall, remaining, isCalled;

        args = arguments;
        stamp = now();
        thisArg = this;
        trailingCall = trailing && (timeoutId || !leading);

        if (maxWait === false) {
            leadingCall = leading && !timeoutId;
        } else {
            if (!maxTimeoutId && !leading) {
                lastCalled = stamp;
            }
            remaining = maxWait - (stamp - lastCalled);
            isCalled = remaining <= 0 || remaining > maxWait;

            if (isCalled) {
                if (maxTimeoutId) {
                    maxTimeoutId = window.clearTimeout(maxTimeoutId);
                }
                lastCalled = stamp;
                result = func.apply(thisArg, args);
            } else if (!maxTimeoutId) {
                maxTimeoutId = window.setTimeout(maxDelayed, remaining);
            }
        }
        if (isCalled && timeoutId) {
            timeoutId = window.clearTimeout(timeoutId);
        } else if (!timeoutId && wait !== maxWait) {
            timeoutId = window.setTimeout(delayed, wait);
        }
        if (leadingCall) {
            isCalled = true;
            result = func.apply(thisArg, args);
        }
        if (isCalled && !timeoutId && !maxTimeoutId) {
            args = thisArg = null;
        }
        return result;
    }
    debounced.cancel = cancel;
    debounced.pending = pending;

    if (typeof func !== 'function') {
        throw new TypeError('this is not a function!');
    }
    wait = wait < 0 ? 0 : +wait || 0;
    if (options === true) {
        leading = true;
        trailing = false;
    } else if (isObject(options)) {
        leading = options.leading;
        maxWait = 'maxWait' in options && Math.max(+options.maxWait || 0, wait);
        trailing = 'trailing' in options ? options.trailing : trailing;
    }

    return debounced;
}

/**
 * Wraps a function, that when invoked repeatedly will only execute once every [wait] number of seconds.
 * (This functionality was primarily copied from underscorejs - http://underscorejs.org/)
 * @private
 * @param {Function} fn The function that should be called at the appropriate interval
 * @param {Integer} wait The number of miliseconds to wait between each call of fn
 */
function throttle(fn, wait) {
    var context, args, timeout, result;
    var previous = 0;
    var later = function () {
        previous = new Date();
        timeout = null;
        result = fn.apply(context, args);
    };
    return function () {
        var now = new Date();
        var remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0) {
            if (timeout) {
                clearTimeout(timeout);
            }
            previous = now;
            result = fn.apply(context, args);
        } else if (!timeout) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
}

/**
 * Wraps a function with `debounce`, but it passes `Date.now()` and `performance.now()` to the function,
 * so that the function can know when it was called
 * @param {Function} fn
 * @param {Number} wait
 * @param {Object} options
 * @type {debounce}
 */
function debounceWithTime(fn, wait, options) {
    const debounced = debounce(fn, wait, options);

    function wrapper(...args) {
        const d = Date.now();
        const p = window.performance.now();

        return debounced(...args, d, p);
    }

    for (const key in debounced) {
        if (typeof debounced[key] === 'function') {
            wrapper[key] = debounced[key];
        }
    }

    return wrapper;
}

let siteProductionDomain;
function getCurrentSiteDomain() {
    if (!siteProductionDomain) {
        const connicalLink = document.querySelector("link[rel='canonical']");
        const parse = document.createElement('a');
        parse.href = connicalLink.href;
        siteProductionDomain = parse.hostname;
    }
    return siteProductionDomain;
}

/**
 * Is the given href is an internal or external URL?
 */
function isExternalLink(href, siteDomain) {
    if (!href) {
        return false;
    }

    if (!siteDomain) {
        siteDomain = getCurrentSiteDomain();
    }

    var re = new RegExp('^https?://([^/]+)/?');
    var matches = href.match(re);
    if (!matches) {
        // relative link
        return false;
    }

    if (matches[1] === siteDomain) {
        // absolute link to url on this site
        return false;
    }
    return true;
}

let _supportsPassive;

/**
 * Detect if the browser support passive event listeners.
 *
 * Feature detection code from documentation:
 * https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md
 */
function supportPassiveEventListeners() {
    if (_supportsPassive === undefined) {
        _supportsPassive = false;
        try {
            var opts = Object.defineProperty({}, 'passive', {
                get: function () {
                    _supportsPassive = true;
                    return null;
                },
            });
            window.addEventListener('testPassive', null, opts);
            window.removeEventListener('testPassive', null, opts);
        } catch (e) {}
    }
    return _supportsPassive;
}

class Timer {
    constructor(callback, delay) {
        this.callback = callback;
        this.timerId = null;
        this.restart(delay);
    }

    restart(delay) {
        this.remaining = delay;
        this.resume();
    }

    resume() {
        this.start = new Date();
        clearTimeout(this.timerId);
        this.timerId = setTimeout(this.callback, this.remaining);
    }

    pause() {
        clearTimeout(this.timerId);
        this.remaining -= new Date() - this.start;
    }

    cancel() {
        clearTimeout(this.timerId);
        this.cancelled = true;
    }
}

function personalizedSlice(hash, personalizedSlices) {
    // use 3rd and 4th last digits of hash for determining A/B test slice
    // for in-content recommendations. RCW uses the last two digits of hash
    const projectedHash = ((hash / 100) | 0) % 100;
    // next line copied from https://stackoverflow.com/questions/20477177/creating-an-array-of-cumulative-sum-in-javascript/55261098#55261098
    const cumulativeSum = (sum) => (value) => (sum += value);
    const cumulativePercentages = (personalizedSlices || [])
        .map((slice) => slice.percentageForSlice)
        .map(cumulativeSum(0));
    const applicableSliceIndex = cumulativePercentages.findIndex((percentage) => percentage > projectedHash);
    return applicableSliceIndex === -1 ? undefined : personalizedSlices[applicableSliceIndex];
}

/**
 * Maps component style definition to CSS Custom Variables suitable for inline
 * element styles in the template.
 * @example
 *
 *  styleObjectToCustomCSSVariables({
 *      itemOne: "#000000",
 *      itemTwo: {
 *          subOne: "red",
 *          subTwo: {
 *              subItem: "rgba(0,0,0,0.5)"
 *          }
 *      },
 *      itemThree: "blue",
 *      // ignores arrays
 *      itemFour: [{ hello: "world"}],
 *  });
 *
 * // returns
 *  {
 *      "--item_one": "#000000",
 *      "--item_two_sub_one": "red",
 *      "--item_two_sub_two_sub_item": "rgba(0,0,0,0.5)",
 *      "--item_three": "blue"
 *  }
 *
 * @param {Object} obj - Component background definition from the component config.
 * @param {string} prefix - Optional variable prefix.
 */
function styleObjectToCustomCSSVariables(obj, prefix) {
    prefix = prefix ? `${camelCaseToUnderscore(prefix)}_` : '--';
    let variables = {};
    if (typeof obj === 'object' && Object.keys(obj) && Object.keys(obj).length) {
        Object.keys(obj).forEach((item) => {
            if (typeof obj[item] === 'string' || typeof obj[item] === 'number') {
                variables[`${prefix}${camelCaseToUnderscore(item)}`] = obj[item];
            } else if (typeof obj[item] === 'object' && !Array.isArray(obj[item])) {
                const _variables = styleObjectToCustomCSSVariables(
                    obj[item],
                    `${prefix}${camelCaseToUnderscore(item)}`
                );
                variables = {
                    ...variables,
                    ..._variables,
                };
            }
        });
    }
    return variables;
}

function camelCaseToUnderscore(str) {
    return str
        .split(/(?=[A-Z])/)
        .join('_')
        .toLowerCase();
}

/**
 * @param {?String} param param string
 * @returns {?String} param value or null
 */

function getQueryParamValue(param) {
    if (!param) {
        return null;
    }
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams?.get(param);
}

/**
 *
 * @param {?Object} slotModel optional object containing slot definition
 * @returns {String} slot info string
 */
function extractSlotInfo(slotModel) {
    if (!slotModel?.zone) {
        return '[Slot Info Unavailable]';
    }

    return `[${slotModel?.zone}_${slotModel?.index}]`;
}

/**
 *
 * @param {string} algorithm used to hash the string
 * @param {string} string string to hash
 * @returns {Promise<String>} string hashed converted to hexa
 */
async function hashStringWithAlg(algorithm, string) {
    const { subtle } = globalThis.crypto;
    const encodedEmail = new TextEncoder().encode(string);
    const hashedEmail = await subtle.digest(algorithm, encodedEmail);
    const hashedEmailArray = Array.from(new Uint8Array(hashedEmail));
    const hashedEmailHex = hashedEmailArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashedEmailHex;
}

/**
 *
 * @param {name} the name of the cookie
 * @returns {String|undefined} the value of the cookie looked for
 */
function getCookieValue(name) {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    if (cookie) {
        // Value may contain equals sign, we only want to split on the first
        return cookie.split(/=(.+)$/)[1];
    }
}

module.exports = {
    addScriptResource,
    addScriptResourceOnce,
    debounce,
    debounceWithTime,
    getCurrentBreakpoint,
    getQueryParamValue,
    isExternalLink,
    makeIntersectionObserver,
    observeBreakpointChanges,
    observeFirstIntersection,
    observeVisibilityChanges,
    observeAfterFirstFullView,
    parseHostName,
    personalizedSlice,
    styleObjectToCustomCSSVariables,
    supportPassiveEventListeners,
    throttle,
    Timer,
    userHasPremium,
    waitForElement,
    whenDomContentLoaded,
    whenPageLoaded,
    extractSlotInfo,
    hashStringWithAlg,
    getCookieValue,
};
